// Custom space roles: what each permission means, the colours on offer, and checks. Roles
// are ordered, highest first, and the order is the hierarchy (HRTH-139): the owner is above
// every role, members without one below. Each member holds at most one.
import type { Perm, SpaceInfo, SpaceMember, SpaceRole } from '../api/types'

export const PERMS: { perm: Perm; label: string; hint: string }[] = [
  { perm: 'invite_members', label: 'Invite friends', hint: 'Make invite links to the space' },
  { perm: 'manage_channels', label: 'Manage channels', hint: 'Create, rename and delete channels' },
  { perm: 'rename_space', label: 'Rename space', hint: 'Change the space name' },
  { perm: 'remove_members', label: 'Remove members', hint: 'Remove members below this role. Includes renaming, since removing someone re-encrypts the names' },
  { perm: 'manage_repositories', label: 'Manage repositories', hint: 'Protect branches of git repositories shared with the space' },
  { perm: 'hide_messages', label: 'Hide messages', hint: "Hide messages of people below this role for everyone, and reveal or un-hide them" },
  { perm: 'suspend_members', label: 'Suspend members', hint: 'Stop someone below this role sending messages, joining calls or parties here for a while' },
  { perm: 'lift_suspensions', label: 'Lift suspensions', hint: 'End a suspension early' },
  { perm: 'resolve_reports', label: 'Resolve reports', hint: 'Mark reports resolved once dealt with' },
  { perm: 'manage_roles', label: 'Manage roles', hint: 'Create, edit, reorder and give out roles below this one, with permissions it has' },
]

/** Permissions that make someone a moderator: they see the space's reports. */
export const MODERATION: Perm[] = ['remove_members', 'hide_messages', 'suspend_members', 'lift_suspensions', 'resolve_reports']

/** Permissions a permission depends on (removing someone re-encrypts the names). */
export const REQUIRES: Partial<Record<Perm, Perm[]>> = { remove_members: ['rename_space', 'manage_channels'] }

/** Adds or removes a permission, keeping dependencies: adding pulls in what it needs. */
export function togglePerm(perms: readonly Perm[], p: Perm): Perm[] {
  if (perms.includes(p)) return perms.filter((x) => x !== p)
  return [...new Set([...perms, p, ...(REQUIRES[p] ?? [])])]
}

/** A permission that another held permission depends on can't be unticked on its own. */
export function isLocked(perms: readonly Perm[], p: Perm): boolean {
  return perms.some((h) => REQUIRES[h]?.includes(p))
}

/**
 * Whether a member may hand out a new space key: whoever can remove people (the owner, and
 * holders of a role with "remove members"), since removing someone rotates the key.
 */
export function mayRotateKey(space: SpaceInfo, user: string): boolean {
  return memberCan(space, user, 'remove_members')
}

/** Swatches that sit with the monochrome palette: ink, greys, signal red, voice green, two accents. */
export const ROLE_COLORS = ['#f4f4f4', '#9a9a9a', '#ff2a1a', '#2bff6a', '#ffb000', '#3d7bff']

/** Whether I may do `perm` in this space (the owner may do everything). */
export function can(space: SpaceInfo | null | undefined, perm: Perm): boolean {
  if (!space) return false
  if (space.my_role === 'owner') return true
  return (space.my_perms ?? []).includes(perm)
}

/** Whether a member of the space may use `perm` (the owner may do everything). */
export function memberCan(space: SpaceInfo, user: string, perm: Perm): boolean {
  const m = space.members.find((x) => x.profile.user_id === user)
  if (!m) return false
  if (m.role === 'owner') return true
  return roleOf(space, m)?.perms.includes(perm) ?? false
}

/** The owner's rank: above every role. */
export const OWNER_RANK = -1
/** The rank of members without a role: below every role. */
export const NO_ROLE_RANK = Number.POSITIVE_INFINITY

/** Where a member stands, lower being higher: the owner, their role's place, then no role. */
export function rankOf(space: SpaceInfo, m: SpaceMember): number {
  if (m.role === 'owner') return OWNER_RANK
  const i = (space.roles ?? []).findIndex((r) => m.roles?.includes(r.id))
  return i < 0 ? NO_ROLE_RANK : i
}

/**
 * Whether `actor` may use `perm` on `target`, as the server decides: they hold it and rank
 * strictly higher. Never oneself, never the owner.
 */
export function mayModerate(space: SpaceInfo, actor: string, target: string, perm: Perm): boolean {
  if (actor === target) return false
  const a = space.members.find((x) => x.profile.user_id === actor)
  const t = space.members.find((x) => x.profile.user_id === target)
  if (!a || !t) return false
  return rankOf(space, a) < rankOf(space, t) && memberCan(space, actor, perm)
}

/** The custom role a member holds, if any. */
export function roleOf(space: SpaceInfo, m: SpaceMember): SpaceRole | undefined {
  return (space.roles ?? []).find((r) => m.roles?.includes(r.id))
}

/** The custom roles a member holds (at most one), for showing badges. */
export function rolesOf(space: SpaceInfo, m: SpaceMember): SpaceRole[] {
  const r = roleOf(space, m)
  return r ? [r] : []
}

/**
 * How far I reach managing roles: roles and members ranked below this. The owner and server
 * admins reach everything below the owner; holders of Manage roles reach below their role.
 * `null` if I can't manage roles.
 */
export function roleReach(space: SpaceInfo, me: string | null | undefined): number | null {
  if (!space.can_manage_roles) return null
  const m = space.members.find((x) => x.profile.user_id === me)
  if (!m || m.role === 'owner' || !roleOf(space, m)?.perms.includes('manage_roles')) return OWNER_RANK
  return rankOf(space, m)
}

/** Permissions I may give a role: all of them for the owner and server admins, else mine. */
export function grantable(space: SpaceInfo, me: string | null | undefined): Perm[] {
  return roleReach(space, me) === OWNER_RANK ? PERMS.map((p) => p.perm) : (space.my_perms ?? [])
}
